import { useMutation } from '@apollo/client';
import React, { useContext, useState } from 'react';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import { ISortProperty } from '../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import { EDTextEdit } from '../../../../../../BaseComponents/TextEdit/EDTextEdit';
import { TVisningstyp } from '../../../../../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataSet/BusinessDataSet';
import { RutinSettingsContext } from '../../../../../../Features/Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import {
  ActiveRoutineContext,
  columnDefsToColumnStr,
  getActiveRoutineQueryVariables,
} from '../../../../../../Features/Routing/ActiveRoutine/ActiveRoutineContext';
import { IColumnDef } from '../../../../../../Types/ColumnDefType';
import { NyaAvenySpecificListLayoutValuesType } from '../../../../../../Types/ListLayoutTypes';
import {
  dbSettingsInnehallValueName,
  nyaAvenyDBSettingsValueName,
  padRoutineId,
} from '../../../../../../Utils/dbSettingsUtils';
import { ActiveDatabaseContext } from '../../../../../ActiveDatabaseContext/ActiveDatabaseContext';
import { userRoutineLayoutsSettingSectionName } from '../KolumnSettingSelect/KolumnSettingSelect';
import saveUserRoutineLayoutMutation from './Mutations/SaveUserRoutineLayoutMutation.gql';
import {
  SaveUserRoutineLayoutMutation,
  SaveUserRoutineLayoutMutationVariables,
} from './Mutations/_QueryTypes/SaveUserRoutineLayoutMutation';
import activeRoutineQuery from '../../../../../../Features/Routing/ActiveRoutine/ActiveRoutineQuery.gql';
import {
  ActiveRoutineQuery,
  ActiveRoutineQueryVariables,
} from '../../../../../../Features/Routing/ActiveRoutine/_QueryTypes/ActiveRoutineQuery';
import { assocPath, clone, update } from 'ramda';
import mergeSort, {
  stringCompareNumeric,
} from '../../../../../../Utils/mergeSort';
import { ErrorDialogContext } from '../../../../../../BaseComponents/ErrorDialogManager/ErrorDialogManager';
import moveUserRoutineLayoutMutation from './Mutations/MoveUserRoutineLayoutMutation.gql';
import {
  MoveUserRoutineLayoutMutation,
  MoveUserRoutineLayoutMutationVariables,
} from './Mutations/_QueryTypes/MoveUserRoutineLayoutMutation';
import EDKeybind from '../../../../../../BaseComponents/Keybinds/EDKeybind';
import { KodplanContext } from '../../../../../KodplanManager/Context/KodplanContext';

interface IProps {
  title: string;
  onClose: () => void;
  open: boolean;
  selectedKolumnSetting?: string | null;
  setSelectedKolumnSetting: (id: string | null) => void;
  valdaKolumner: readonly IColumnDef[];
  showDetailsRight: boolean;
  sortOrder: ISortProperty[];
  visningstyp: TVisningstyp;
  collectionName: string;
}

const getNyaAvenyValues = (
  nyaAvenySpecificValues: NyaAvenySpecificListLayoutValuesType
): string => {
  return JSON.stringify(nyaAvenySpecificValues);
};

const SaveKolumnSettingDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const { activeRoutine } = useContext(RutinSettingsContext);
  const activeRoutineId = activeRoutine?.routine?.rutinID;
  const errorDialogContext = useContext(ErrorDialogContext);
  const activeRoutineContext = useContext(ActiveRoutineContext);
  const kodplanContext = useContext(KodplanContext);

  const selectedKolumnSetting = activeRoutineContext?.sparadeLayouter?.find(
    sparadLayout => sparadLayout.id === props.selectedKolumnSetting
  );

  const [name, setName] = useState(selectedKolumnSetting?.name ?? '');
  const [description, setDescription] = useState(
    selectedKolumnSetting?.description ?? ''
  );

  const [saveUserRoutineLayout, { loading: saveUserRoutineLayoutLoading }] =
    useMutation<
      SaveUserRoutineLayoutMutation,
      SaveUserRoutineLayoutMutationVariables
    >(saveUserRoutineLayoutMutation, {
      onCompleted: data => {
        props.onClose();
        if (data.savedbsettings?.[0].id) {
          props.setSelectedKolumnSetting(data.savedbsettings[0].id);
        }
      },
      update: (cache, result) => {
        const activeRoutineQueryVariables = getActiveRoutineQueryVariables(
          activeDatabaseContext.activeDatabase,
          activeRoutineId,
          props.collectionName,
          kodplanContext.activeKodplan?.id
        );
        const oldData = cache.readQuery<
          ActiveRoutineQuery,
          ActiveRoutineQueryVariables
        >({
          query: activeRoutineQuery,
          variables: activeRoutineQueryVariables,
        });

        const savedRoutineLayout = result.data?.savedbsettings?.[0];

        if (oldData && savedRoutineLayout) {
          const routineLayoutIndex =
            oldData?.me?.[0]?.routineLayouts?.findIndex(
              routineLayout =>
                routineLayout.name === userRoutineLayoutsSettingSectionName
            );

          const oldRoutineLayoutSection = oldData?.me?.[0]?.routineLayouts?.[
            routineLayoutIndex ?? -1
          ] ?? {
            __typename: 'TEDSettingsSection',
            name: userRoutineLayoutsSettingSectionName,
            id: userRoutineLayoutsSettingSectionName,
            sections: [],
          };

          const isNewRoutineLayout = !oldRoutineLayoutSection?.sections?.some(
            section => section.id === savedRoutineLayout.id
          );

          let newSections = clone(oldRoutineLayoutSection.sections ?? []);
          if (isNewRoutineLayout) {
            newSections.push(savedRoutineLayout);
          }
          newSections = mergeSort(newSections, (a, b) => {
            return stringCompareNumeric(a.name ?? '', b.name ?? '');
          });

          const newRoutineLayoutSection = assocPath(
            ['sections'],
            newSections,
            oldRoutineLayoutSection
          );

          const newData = assocPath(
            ['me', 0, 'routineLayouts'],
            routineLayoutIndex
              ? update(
                  routineLayoutIndex,
                  newRoutineLayoutSection,
                  oldData?.me?.[0]?.routineLayouts ?? []
                )
              : [newRoutineLayoutSection],
            oldData
          );

          cache.writeQuery<ActiveRoutineQuery, ActiveRoutineQueryVariables>({
            query: activeRoutineQuery,
            data: newData,
            variables: activeRoutineQueryVariables,
          });
        }
      },
      onError: error => {
        errorDialogContext.error('Kunde inte spara inställning', error);
      },
    });

  const [moveUserRoutineLayout, { loading: moveUserRoutineLayoutLoading }] =
    useMutation<
      MoveUserRoutineLayoutMutation,
      MoveUserRoutineLayoutMutationVariables
    >(moveUserRoutineLayoutMutation, {
      onError: error => {
        errorDialogContext.error('Kunde inte döpa om inställning', error);
      },
    });

  const loading = saveUserRoutineLayoutLoading || moveUserRoutineLayoutLoading;

  const saveDisabled = loading || !name;

  const save = () => {
    const dbName = activeDatabaseContext.activeDatabase;

    if (dbName && activeRoutineId) {
      const saveRoutineLayoutVariables: SaveUserRoutineLayoutMutationVariables =
        {
          dbName,
          dbSettingsInnehallValueName,
          nyaAvenyDBSettingsValueName,
          sectionName: `Forms/${padRoutineId(
            activeRoutineId
          )}/${userRoutineLayoutsSettingSectionName}/${name}`,
          vtInnehallValue: columnDefsToColumnStr(
            props.valdaKolumner.filter(col => !col.disableSave)
          ),
          nyaAvenyValues: getNyaAvenyValues({
            description: description,
            showDetailsRight: props.showDetailsRight,
            sortering: props.sortOrder,
            visningstyp: props.visningstyp,
          }),
        };

      if (
        selectedKolumnSetting != null &&
        selectedKolumnSetting.name !== name
      ) {
        moveUserRoutineLayout({
          variables: {
            dbName,
            sectionName: `Forms/${padRoutineId(
              activeRoutineId
            )}/${userRoutineLayoutsSettingSectionName}/${
              selectedKolumnSetting.name
            }`,
            newSectionName: saveRoutineLayoutVariables.sectionName,
          },
        }).then(result => {
          if (!result.errors) {
            saveUserRoutineLayout({
              variables: saveRoutineLayoutVariables,
            });
          }
        });
      } else {
        saveUserRoutineLayout({
          variables: saveRoutineLayoutVariables,
        });
      }
    }
  };

  return (
    <EDKeybind
      keymap="Enter"
      onKeyDown={() => {
        if (!saveDisabled) {
          save();
        }
      }}
      global
    >
      <EDDialog
        title={props.title}
        onClose={props.onClose}
        open={props.open}
        maxWidth={'sm'}
        fullWidth
        onEnter={() => {
          setName(selectedKolumnSetting?.name ?? '');
          setDescription(selectedKolumnSetting?.description ?? '');
        }}
        footerActions={[
          {
            label: 'Avbryt',
            onClick: () => {
              props.onClose();
            },
          },
          {
            label: loading ? 'Sparar' : 'Spara',
            onClick: () => {
              save();
            },
            buttonProps: {
              disabled: saveDisabled,
              disabledReason: loading
                ? undefined
                : !name
                ? 'Måste ange namn'
                : undefined,
            },
          },
        ]}
      >
        <EDTextEdit
          label="Namn"
          onChange={newValue => {
            setName(newValue);
          }}
          value={name}
          required
          autoFocus={!name}
        />
        <EDTextEdit
          label="Beskrivning"
          onChange={newValue => {
            setDescription(newValue);
          }}
          multiline
          value={description}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.stopPropagation();
            }
          }}
          autoFocus={Boolean(name) && !description}
        />
      </EDDialog>
    </EDKeybind>
  );
};

export default SaveKolumnSettingDialog;
